import type {
  BookSpaceRequest,
  BookSpaceResponse,
  Desk,
  DesksApiResponse,
  DesksParams,
  Employee,
  OfficeSpace,
  PlacesResponse,
} from '../model/types'
import { createHttpClient, type HttpRequestOptions } from 'shared/api/createHttpClient'

export function normalizeMapSpaceId(value: string): string {
  return value
    .normalize('NFKC')
    .trim()
    .replace(/[\u2010-\u2015\u2212]/g, '-')
    .replace(/[_\s]+/g, '-')
    .replace(/-+/g, '-')
    .toUpperCase()
}

function toMapSpaceId(identifier: string): string {
  return normalizeMapSpaceId(identifier)
}

function toOfficeSpace(desk: Desk): OfficeSpace {
  const employee: Employee | null = desk.booked_user ?? desk.assigned_user
  const bookingId = desk.booking_id
  return {
    id: toMapSpaceId(desk.identifier),
    deskId: desk.id,
    status: desk.status,
    employee,
    assignedUser: desk.assigned_user,
    bookedUser: desk.booked_user,
    identifier: desk.identifier,
    label: desk.label,
    bookingId,
    freeDatesText: desk.free_dates_text,
    hasChangedDesk: desk.has_changed_desk,
  }
}

function toDeskUrl(params: DesksParams = {}): string {
  const query = new URLSearchParams()
  if (typeof params.page === 'number') query.set('page', String(params.page))
  if (params.date) query.set('date', params.date)
  if (typeof params.available_only === 'boolean') {
    query.set('available_only', String(params.available_only))
  }
  const suffix = query.toString()
  return `/work/desks/${suffix ? `?${suffix}` : ''}`
}

export async function fetchPlaces(
  params: DesksParams = {},
  options?: HttpRequestOptions
): Promise<PlacesResponse> {
  const client = createHttpClient()
  const response = await client.get<DesksApiResponse | Desk[]>(toDeskUrl(params), options)
  const desks = Array.isArray(response) ? response : (response.results ?? [])
  return { spaces: desks.map(toOfficeSpace) }
}

/**
 * Books today's desk. The backend reuses this endpoint for the single allowed
 * daily seat change: posting another free desk releases the previous one and
 * keeps the same booking id. There is no user-facing cancel endpoint.
 */
export async function bookSpace(payload: BookSpaceRequest): Promise<BookSpaceResponse> {
  const client = createHttpClient()
  return client.post<BookSpaceResponse>(`/work/desks/${payload.deskId}/book/`, {
    date: payload.date,
  })
}
